import { Reducer, Action } from 'redux';

/**
 * Format of customer actions provided for the reducer
 *
 * @export
 * @interface ICustomReducers
 * @template CST The customer state shape
 * @template ACT The customer action payload
 */
export interface ICustomReducers<CST, PAYLOAD = any> {
  [action: string]: Reducer<CST|null, Action<PAYLOAD>>;
}

/**
 * Creates a typed custom reducer function
 *
 * @export
 * @template CST
 * @template PAYLOAD
 * @param {((state: CST|undefined, action: Action<PAYLOAD>) => CST)} reducerFunc
 * @returns {Reducer<CST, Action<PAYLOAD>>}
 */
export function createCustomReducer<CST, ACTION extends Action<any>>(
  reducerFunc: (state: CST|undefined, action: ACTION) => CST,
): Reducer<CST, ACTION> {
  const customReducer: Reducer<CST, ACTION> = (state, action) => {
    return reducerFunc(state, action);
  };

  return customReducer;
}

/**
 * Combine all custom reducers for a single state key
 *
 * @export
 * @template CST The custom state shape
 * @param {ICreateCustomReducerOptions} options The required options for creating custom reducers
 * @returns {Reducer<IEvaServiceState<CST>|undefined>}
 */
export function combineCustomReducers<CST, PAYLOAD = any>(
  reducers: ICustomReducers<CST, PAYLOAD>,
): Reducer<CST|null, Action<PAYLOAD>> {
  const reducer: Reducer<CST|null, Action<PAYLOAD>> = (state = null, action) => {

    const actionReducer = reducers[action.type as unknown as string];
    if (actionReducer) {
      return actionReducer(state, action);
    }

    return state;
  };

  return reducer;
}
